"use client";

import { css } from "styled-system/css";
import type { ReactNode } from "react";
import { useEffect } from "react";

const overlay = css({
  position: "fixed",
  inset: 0,
  bg: "rgba(0, 0, 0, 0.45)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 50,
  p: "4",
});

const panel = css({
  bg: "bg.card",
  color: "fg.DEFAULT",
  rounded: "xl",
  width: "100%",
  p: "6",
  boxShadow: "xl",
  borderWidth: "1px",
  borderColor: "border.DEFAULT",
  maxH: { base: "calc(100vh - 2rem)", md: "calc(100vh - 4rem)" },
  overflowY: "auto",
});

const sizeMap: Record<"sm" | "md" | "lg" | "xl" | "2xl" | "3xl" | "4xl", string> = {
  sm: "32rem",
  md: "36rem",
  lg: "40rem",
  xl: "48rem",
  "2xl": "56rem",
  "3xl": "64rem",
  "4xl": "72rem",
};

export function Modal({
  open,
  onClose,
  title,
  children,
  size = "lg",
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  size?: "sm" | "md" | "lg" | "xl" | "2xl" | "3xl" | "4xl";
}) {
  useEffect(() => {
    if (!open) return;
    const h = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className={overlay}
      role="dialog"
      aria-modal
      aria-labelledby="modal-title"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className={panel} style={{ maxWidth: sizeMap[size] }}>
        <h2 id="modal-title" className={css({ fontSize: "lg", fontWeight: "bold", mb: "4" })}>
          {title}
        </h2>
        {children}
      </div>
    </div>
  );
}
